import { TranslateService } from '@ngx-translate/core';

import { CmpsDatePipe } from '../../../common/date.pipe';
import { CmpsDecimalPipe, CmpsPercentPipe } from '../../../common/number.pipe';
import { CellNode } from '../../../insight/experimental.grid';
import { User } from '../../../user/user.model';
import { Column } from '../../experimental.grid';
import { CmpsNumberWithSign } from '../../numberWithSign.pipe';
import { TranslationKeyMap } from '../../scorecard.i18n';

import {
    CellContext,
    ColumnContext,
    retrieveContributionCellData,
} from './direct-report-contribution-grid.model';
import { RankColumnType } from './direct-report-rank-grid.model';
import { retrieveCellData } from './direct-report-scorecard-grid.model';
import { FilterCategory } from './filter-category.model';

const initialValue = '-';

export enum TemplateID {
    HEADER_GROUP = 'headerGroup',
    HEADER = 'header',
    CONTENT = 'content',
}

export enum ColumnType {
    ACTUAL,
    TARGET,
    TARGET_PERCENT,
    POINTS_BAND,
    WEIGHT,
    ADJUSTED_POINTS,
    RANK,
    USER_NAME,
    SUPERGROUP_NAME,
    PERIOD,
    PILLAR_NAME,
    DIRECT_REPORTS,
    OVERALL_SCORE,
    SITE_SUPERGROUP_RANK,
    ROLE_OVERALL_RANK,
    ROLE_BY_REGION_RANK,
    SUPERGROUP_OVERALL_RANK,
    WEIGHTED,
    FYI,
    CONDITION,
    MODIFIER,
    QUALIFIER,
    ISINEFFECT,
    QUALIFIERMET,
    PILLAR = 9000,
    METRIC_NAME,
    METRIC_RANK,
}
export enum DrsColumnType {
    WEIGHTED_METRICS = 9995,
    FYI_METRICS = 9996,
    CONDITION_METRICS = 9997,
    MODIFIER_METRICS = 9998,
    QUALIFIER_METRICS = 9999,
}

export interface FilterPillar {
    name?: string;
    isSelected?: boolean;
    columnId?: string;
    value?: any;
    label?: string;
    disabled?: boolean;
    category?: FilterCategory;
}

export interface ScorecardSelection {
    userId?: number;
    user?: User;
    scorecardId?: number;
    groupId?: number;
    endDate?: Date;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getMetricLabelValue(): string {
    return initialValue;
}

export function valueForColumn(
    columnType: ColumnType,
    metric: CellContext,
    c: ColumnContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val;
    switch (columnType) {
        case ColumnType.USER_NAME:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['userName'] || initialValue;
            break;
        case ColumnType.SUPERGROUP_NAME:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['superGroupName'] || initialValue;
            break;
        case ColumnType.PERIOD:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['period'] || initialValue;
            break;
        default:
            val =
                decimalPipe.transform(metric.data[c.metricId], '1.2-2') ||
                initialValue;
            break;
    }

    return val;
}

export function valueForContributionColumn(
    columnType: ColumnType,
    metric: CellContext,
    c: ColumnContext,
    decimalPipe: CmpsDecimalPipe,
): string {
    let val;
    switch (columnType) {
        case ColumnType.USER_NAME:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data[0]['userName'] || initialValue;
            break;
        case ColumnType.SUPERGROUP_NAME:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data[0]['superGroupName'] || initialValue;
            break;
        case ColumnType.PERIOD:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data[0]['period'] || initialValue;
            break;
        default:
            val =
                retrieveContributionCellData(metric.data, c.metricId) ||
                initialValue;
            break;
    }

    return val;
}

export function exportContributionGetValue(
    l: CellNode,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
): any {
    const metric = l.context.ctx;
    const columnType = l.context.c.ctx.type as ColumnType;
    const c = l.context.c;
    let val;
    if (metric.templateId === TemplateID.HEADER_GROUP && metric.type) {
        val = translateService.instant(metric.type);
    }
    if (metric.templateId === TemplateID.HEADER && metric.metricName) {
        val = translateService.instant(metric.metricName);
    }
    if (
        metric.templateId !== TemplateID.HEADER_GROUP &&
        metric.templateId !== TemplateID.HEADER
    ) {
        val = valueForContributionColumn(
            columnType,
            metric,
            c.ctx,
            decimalPipe,
        );
    }
    return val;
}

export function exportRankGetValue(
    l: CellNode,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
    datePipe: CmpsDatePipe,
    numberWithSignPipe: CmpsNumberWithSign,
): any {
    const metric = l.context.ctx;
    const columnType = l.context.c.ctx.type as RankColumnType;
    const nameOverride = l.context.c.ctx.nameOverride;
    let val;
    if (metric.templateId === 'header') {
        val = getHeaderValue(columnType, translateService, nameOverride);
    } else {
        val = valueForRankColumn(
            columnType,
            metric,
            translateService,
            decimalPipe,
            percentPipe,
            datePipe,
            numberWithSignPipe,
        );
    }

    return val;
}

export function valueForRankColumn(
    columnType: RankColumnType,
    metric: CellContext,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
    dataPipe: CmpsDatePipe,
    numberWithSignPipe: CmpsNumberWithSign,
): string {
    let val;

    switch (columnType) {
        case RankColumnType.DIRECT_REPORTS:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['directReport'] || initialValue;
            break;
        case RankColumnType.SUPERGROUP_NAME:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['subSuperGroupName'] || initialValue;
            break;
        case RankColumnType.PERIOD:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['period'] || initialValue;
            break;
        case RankColumnType.ADJUSTED_POINTS:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['overallScore']
                ? // eslint-disable-next-line @typescript-eslint/dot-notation
                  decimalPipe.transform(metric.data['overallScore'], '1.2-2')
                : initialValue;
            break;
        case RankColumnType.SUPERGROUP_OVERALL_RANK:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['superGroupOverallRank'] || initialValue;
            break;
        case RankColumnType.SITE_SUPERGROUP_RANK:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['siteSuperGroupRank'] || initialValue;
            break;
        case RankColumnType.ROLE_OVERALL_RANK:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['roleOverallRank'] || initialValue;
            break;
        case RankColumnType.ROLE_BY_REGION_RANK:
            // eslint-disable-next-line @typescript-eslint/dot-notation
            val = metric.data['roleByRegionRank'] || initialValue;
            break;
    }

    return val;
}

function getHeaderValue(
    columnType: RankColumnType,
    translateService: TranslateService,
    nameOverride: any,
): string {
    if (columnType === RankColumnType.DIRECT_REPORTS) {
        return translateService.instant(TranslationKeyMap._9128_DIRECTREPORTS);
    }

    if (columnType === RankColumnType.SUPERGROUP_NAME) {
        return translateService.instant(TranslationKeyMap._67_SUPERGROUP);
    }

    if (nameOverride) {
        return nameOverride;
    }

    switch (columnType) {
        case RankColumnType.PERIOD: {
            return translateService.instant(TranslationKeyMap._773_PERIOD);
        }
        case RankColumnType.ADJUSTED_POINTS: {
            return translateService.instant(
                TranslationKeyMap._962_OVERALL_SCORE,
            );
        }
        case RankColumnType.SUPERGROUP_OVERALL_RANK: {
            return translateService.instant(
                TranslationKeyMap._9124_SUPERGROUP_OVERALL_RANK,
            );
        }
        case RankColumnType.SITE_SUPERGROUP_RANK: {
            return translateService.instant(
                TranslationKeyMap._9125_SITE_SUPERGROUP_RANK,
            );
        }
        case RankColumnType.ROLE_OVERALL_RANK: {
            return translateService.instant(
                TranslationKeyMap._9126_ROLE_OVERALL_RANK,
            );
        }
        case RankColumnType.ROLE_BY_REGION_RANK: {
            return translateService.instant(
                TranslationKeyMap._9127_ROLE_BY_REGION_RANK,
            );
        }
        default:
            return null;
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function checkNotNull(metric: number | string) {
    return metric != null;
}

export function exportScorecardGetValue(
    l: CellNode,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
): any {
    const metric = l.context.ctx;
    const column = l.context.c as any as Column<ColumnContext>;
    const metricId = column.id;
    let columnType = column.ctx.type as any;
    if (typeof columnType === 'string' && columnType.includes('-')) {
        columnType = columnType.split('-')[0];
    }

    const nameOverride = column.ctx.metricName || column.ctx.pillarName;
    let val = '';
    if (
        (metric.templateId === TemplateID.HEADER_GROUP ||
            metric.templateId === TemplateID.HEADER) &&
        nameOverride
    ) {
        val = translateService.instant(nameOverride);
    }
    if (
        metric.templateId !== TemplateID.HEADER_GROUP &&
        metric.templateId !== TemplateID.HEADER
    ) {
        val = valueForScorecardColumn(
            columnType,
            metricId,
            metric,
            decimalPipe,
            percentPipe,
        );
    }
    return val;
}

export function valueForScorecardColumn(
    columnType: ColumnType,
    metricId: string,
    metric: CellContext,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
): string {
    let val = '';

    switch (metricId) {
        case ColumnType.USER_NAME.toString():
            val = metric.data[0].directReport;
            break;
        case ColumnType.SUPERGROUP_NAME.toString():
            val = metric.data[0].superGroup;
            break;
        case ColumnType.PERIOD.toString():
            val = metric.data[0].period;
            break;
        case ColumnType.OVERALL_SCORE.toString():
            val = metric.data[0].overallScore
                ? decimalPipe.transform(metric.data[0].overallScore, '1.2-2')
                : '-';
            break;
        default:
            // eslint-disable-next-line no-case-declarations
            const mIds = metricId.split('-');
            // eslint-disable-next-line no-case-declarations
            let preVal;
            if (mIds.length < 3) {
                preVal = retrieveCellData(
                    metric.data,
                    columnType.toString(),
                    metricId,
                    'actual',
                );
                if (checkNotNull(preVal)) {
                    val =
                        preVal !== '-'
                            ? decimalPipe.transform(preVal, '1.2-2')
                            : '-';
                }
            } else {
                switch (Number(mIds[2])) {
                    case ColumnType.TARGET:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'target',
                        );
                        if (checkNotNull(preVal)) {
                            val =
                                preVal !== '-'
                                    ? decimalPipe.transform(preVal, '1.2-2')
                                    : '-';
                        }
                        break;
                    case ColumnType.TARGET_PERCENT:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'percentToTarget',
                        );
                        if (checkNotNull(preVal)) {
                            val =
                                preVal !== '-'
                                    ? percentPipe.transform(
                                          preVal / 100,
                                          '1.1-2',
                                      )
                                    : '-';
                        }
                        break;
                    case ColumnType.POINTS_BAND:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'pointBand',
                        );
                        if (checkNotNull(preVal)) {
                            val = preVal !== '-' ? preVal : '-';
                        }
                        break;
                    case ColumnType.WEIGHT:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'weight',
                        );
                        if (checkNotNull(preVal)) {
                            val =
                                preVal !== '-'
                                    ? decimalPipe.transform(preVal, '1.2-2')
                                    : '-';
                        }
                        break;
                    case ColumnType.ADJUSTED_POINTS:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'adjustedPoints',
                        );
                        if (checkNotNull(preVal)) {
                            val =
                                preVal !== '-'
                                    ? decimalPipe.transform(preVal, '1.2-2')
                                    : '-';
                        }
                        break;
                    case ColumnType.METRIC_RANK:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'rank',
                        );
                        if (checkNotNull(preVal)) {
                            val = preVal !== '-' ? preVal : '-';
                        }
                        break;
                    case ColumnType.ISINEFFECT:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'isInEffect',
                        );
                        if (checkNotNull(preVal)) {
                            val = preVal;
                        }
                        break;
                    case ColumnType.QUALIFIERMET:
                        preVal = retrieveCellData(
                            metric.data,
                            columnType.toString(),
                            metricId,
                            'isInEffect',
                        );
                        if (checkNotNull(preVal)) {
                            val = preVal;
                        }
                        break;
                    default:
                        break;
                }
            }
    }
    return val;
}
